import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpHeaders,
  HttpErrorResponse
} from '@angular/common/http';
import { catchError, map, Observable, throwError } from 'rxjs';
import { AlertsService, CLToastType } from '@clavisco/alerts';
import { AuthenticationService } from '../core/services/authentication.service';
import { DataStorageService } from '../core/services/data-storage.service';
import { Router, UrlTree } from '@angular/router';
import { IToken } from '../core/interfaces/i-token';

@Injectable()
export class AuthenticationInterceptor implements HttpInterceptor {

  constructor(
    private alertService: AlertsService,
    private authService: AuthenticationService,
    private dataStorageService: DataStorageService,
    private router: Router
  ) { }

  /**
 * Intercepts the outgoing HTTP requests and adds an authentication token.
 * 
 * This method checks if the request URL is excluded from authentication token 
 * attachment (e.g., assets, token, password-related URLs). If not excluded, 
 * it adds the authentication token in the headers and processes the request.
 * If the user is not authenticated, it redirects them to the login page.
 * 
 * @param request - The HTTP request to be handled.
 * @param next - The next HTTP handler to pass the request to.
 * @returns An observable containing the HTTP response or an error.
 */
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    try {
      const excluedUrls: string[] = ['assets', 'token', 'ChangeRecoverPassword', 'RecoverPassword', 'ChangePassword'];
      if (excluedUrls.some(url => request.url.includes(url))) {
        return next.handle(request);
      }
      request = this.AddAuthenticationToken(request);

      return next.handle(request).pipe(
        map((event: HttpEvent<any>) => {
          return event;
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(error);
        })
      );
    } catch (error) {
      this.alertService.Toast(
        {
          message: 'Su sesión ha expirado, por favor inicie sesión nuevamente',
          type: CLToastType.ERROR,
          duration: 5000
        }
      );
      return throwError(error);
    }
  }

  /**
 * Adds the authentication token to the HTTP request headers if the user is authenticated.
 * If the user is not authenticated, it logs out the user and redirects them to the login page.
 * 
 * @param request - The HTTP request to add the authentication token to.
 * @returns A cloned HTTP request with the authentication token.
 */
  private AddAuthenticationToken(request: HttpRequest<any>): HttpRequest<any> {

    if (!this.authService.IsAuthenticated()) {
      this.authService.Logout();
      const urlTree: UrlTree = this.router.createUrlTree(['/login'], {});
      this.router.navigateByUrl(urlTree);
      throw new Error();
    }

    const TOKEN = this.dataStorageService.GetItem<IToken>('currentUser');

    if (!TOKEN) {
      return request;
    }

    let headers: HttpHeaders = request.headers.set(`Authorization`, `Bearer ${TOKEN.access_token}`);

    request = request.clone({ headers });
    return request;
  }
}
